/**
 * UI module — DOM-overlay film noir interface. Owns #ui-root + #title-card:
 * title card, monologue typewriter bar, clue journal (Tab), crosshair,
 * accuse panel (phone booth), HUD, and win/lose end cards. See CONTRACTS.md.
 */
import { PLAYER_SPAWN_POSITION } from '../core/constants'
import { isTouchDevice } from '../core/platform'
import type { ModuleContext, Vec3 } from '../core/types'
import { createAccusePanel } from './accuse'
import { createCaseLog } from './caseLog'
import {
  CLOSE_KEY,
  JOURNAL_KEY,
  PHONE_TARGET_PATTERN,
  TOUCH_JOURNAL_BUTTON_ID,
  TOUCH_JOURNAL_LABEL,
  UI_ROOT_ID
} from './constants'
import { createCrosshair } from './crosshair'
import { createEndCards } from './endCards'
import { createHud } from './hud'
import { createJournal } from './journal'
import { createMonologueBar } from './monologue'
import { injectStyles } from './styles'
import { setupTitleCard } from './title'

export const initUi = (ctx: ModuleContext): void => {
  const { bus } = ctx
  const root = document.getElementById(UI_ROOT_ID)
  if (!root) {
    throw new Error('GUMSHOE UI: #ui-root not found in index.html')
  }

  injectStyles()

  let titleDismissed = false
  let gameOver = false
  let journalOpen = false
  let playerPosition: Vec3 = PLAYER_SPAWN_POSITION

  const caseLog = createCaseLog(bus)
  const crosshair = createCrosshair(root, ctx)
  createMonologueBar(root, bus)
  createHud(root, bus)
  const journal = createJournal(root, bus, caseLog, () => playerPosition)

  /** Release pointer lock so overlay buttons are clickable. */
  const releasePointer = (): void => {
    if (document.pointerLockElement) document.exitPointerLock()
  }

  const refreshCrosshair = (): void => {
    crosshair.setVisible(titleDismissed && !gameOver && !journalOpen && !accuse.isOpen())
  }

  const accuse = createAccusePanel(root, bus, caseLog, () => playerPosition, () => {
    refreshCrosshair()
  })

  const setJournal = (open: boolean): void => {
    if (journalOpen === open) return
    journalOpen = open
    journal.setOpen(open)
    if (open) releasePointer()
    refreshCrosshair()
    bus.emit('ui:journal-toggle', { open })
  }

  // Touch devices get a persistent journal button — Tab has no thumb.
  if (isTouchDevice()) {
    const journalButton = document.createElement('button')
    journalButton.id = TOUCH_JOURNAL_BUTTON_ID
    journalButton.textContent = TOUCH_JOURNAL_LABEL
    journalButton.addEventListener('click', () => {
      if (!titleDismissed || gameOver) return
      if (accuse.isOpen()) {
        accuse.close()
        return
      }
      setJournal(!journalOpen)
    })
    root.append(journalButton)
  }

  const title = setupTitleCard(bus, () => {
    titleDismissed = true
    refreshCrosshair()
  })

  createEndCards(root, bus, caseLog, () => {
    gameOver = true
    if (journalOpen) setJournal(false)
    accuse.close()
    refreshCrosshair()
    releasePointer()
  })

  bus.on('player:moved', ({ position }) => {
    playerPosition = position
  })

  // Phone booth interaction opens the accuse panel.
  bus.on('player:interact', ({ target }) => {
    if (!titleDismissed || gameOver || target.kind === 'suspect') return
    if (PHONE_TARGET_PATTERN.test(target.id) || PHONE_TARGET_PATTERN.test(target.prompt)) {
      if (journalOpen) setJournal(false)
      accuse.open()
      releasePointer()
      refreshCrosshair()
    }
  })

  // The verdict is incoming — clear interactive overlays.
  bus.on('game:accuse', () => {
    accuse.close()
    if (journalOpen) setJournal(false)
  })

  window.addEventListener('keydown', (event) => {
    if (!titleDismissed) {
      title.dismiss()
      return
    }
    if (gameOver) return
    if (event.key === JOURNAL_KEY) {
      event.preventDefault()
      if (accuse.isOpen()) {
        accuse.close()
        return
      }
      setJournal(!journalOpen)
      return
    }
    if (event.key === CLOSE_KEY) {
      if (accuse.isOpen()) {
        accuse.close()
      } else if (journalOpen) {
        setJournal(false)
      }
    }
  })
}
